// ============================================================
// teamIndex - every team the GM's library wears, and who wears it. Pure.
//
// A team is not a record of its own: it is `character.team` on each sheet
// (engine/roster/team), so the library's teams are found by reading the
// sheets - each character asset's, and each person's inside a scene asset
// (the scene is stored as its file, `scene.cast[].combatant.sheet`). The
// Teams view (plan teams-manager, 2026-10-27) lists what this returns; its
// verbs rewrite the members it names.
// ============================================================
import {
  blockGroups,
  choicesWithLeaders,
  liveryFor,
  playedBlock,
  tagConflicts,
  teamInfoOf,
  teamsIn,
  type Livery,
  type TeamChoice,
  type TagConflict,
  type Team,
  type TeamInfo,
  type BlockGroup,
  type Wearer,
} from '../roster/team';
import { storedPeople } from '../scenes/sceneFile';
import { isWhole } from './rows';
import { readKillTeamBlock } from '../killTeam/block';
import type { AssetRow } from './types';

interface MemberBase extends Wearer {
  assetId: string;
  /** The asset's version this record was read from - what a rewrite builds on. */
  version: number;
  /** The character's, or the person's name in the scene. */
  name: string;
  /** WHO this is: the sheet's own id, the same on a character and on his copies inside scenes (else the member's). */
  person: string;
  folderId: string;
  sheet: { team: TeamInfo };
}

/** A character asset wearing a team; its id is the asset's. */
interface CharacterMember extends MemberBase {
  kind: 'character';
}

/** One person inside a scene asset; id `<assetId>/<castId>`, castId the combatant's id in the stored file. */
interface ScenePerson extends MemberBase {
  kind: 'scene';
  castId: string;
  sceneName: string;
}

/** One place a team record lives: a character asset, or one person in a scene asset. */
export type TeamMember = CharacterMember | ScenePerson;

export type LibraryTeam = Team<TeamMember> & {
  /** Declared, or handed from the palette the way the map hands one - what the view paints. */
  livery: Livery;
  /** The record a sheet joining this team carries: its name, the colour it DECLARED (never a palette one) and side. */
  record: TeamInfo;
  /** The name of the team it fights beside, when that team is in the library; else null. */
  ally: string | null;
  /** How many character assets, and how many people inside scenes, wear it. */
  characters: number;
  inScenes: number;
  /** The tag worn under more than one name or colour, largest group first; null when it is one team. */
  conflict: TagConflict<TeamMember>['groups'] | null;
  /** Who leads its kill-team: the first person whose record says so; null for nobody. */
  leader: { person: string; name: string } | null;
  /** Its wearers by the kill-team block they carry, largest first (`blockGroups`); null when they all agree. */
  blockConflict: BlockGroup<TeamMember>[] | null;
};

/** What the index reads of an asset row: the rest (its picture, size, tags) never changes a team. */
export type TeamRow = Pick<AssetRow, 'id' | 'kind' | 'name' | 'folderId' | 'version' | 'deletedAt'>;

/** The rows that can wear teams - live characters and scenes with a whole version - cut to what the index reads. */
export const teamRowsOf = (rows: readonly AssetRow[]): TeamRow[] =>
  rows
    .filter((r) => isWhole(r) && (r.kind === 'character' || r.kind === 'scene'))
    .map(({ id, kind, name, folderId, version, deletedAt }) => ({ id, kind, name, folderId, version, deletedAt }));

const text = (v: unknown) => (typeof v === 'string' ? v : undefined);

/**
 * A stored team record, checked at the boundary: the index reads sheets RAW (a hand-edited import, an older client),
 * so anything without a string tag is no team, and a field of the wrong type is dropped rather than trusted.
 */
export function readTeam(raw: unknown): TeamInfo | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.tag !== 'string' || !r.tag.trim()) return null;
  const [label, livery, side] = [text(r.label), text(r.livery), text(r.side)];
  const killTeam = readKillTeamBlock(r.killTeam);
  // A livery string of any other shape is kept: the team helpers read it as unpainted (`declared`), as everywhere.
  return {
    tag: r.tag,
    ...(label ? { label } : {}),
    ...(livery ? { livery: livery as TeamInfo['livery'] } : {}),
    ...(side ? { side } : {}),
    ...(killTeam ? { killTeam } : {}),
    ...(r.leads === true ? { leads: true as const } : {}),
  };
}

/** The sheet's own id when it has one: the same person in the library and inside a scene. */
const personOf = (sheet: unknown, fallback: string): string => {
  const id = (sheet as { id?: unknown } | null)?.id;
  return typeof id === 'string' && id ? id : fallback;
};

/** A character asset's member, when its sheet wears a team. */
export function membersOfCharacter(row: TeamRow, sheet: unknown): CharacterMember[] {
  const team = readTeam((sheet as { team?: unknown } | null)?.team);
  if (!isWhole(row) || !team) return [];
  const { id, version, folderId, name } = row;
  return [
    { id, assetId: id, version, kind: 'character', name, person: personOf(sheet, id), folderId, sheet: { team } },
  ];
}

/** Each person in a scene asset who wears a team, one member apiece. */
export function membersOfScene(row: TeamRow, file: unknown): ScenePerson[] {
  if (!isWhole(row)) return [];
  return storedPeople(file).flatMap((c) => {
    const team = readTeam((c.sheet as { team?: unknown } | undefined)?.team);
    if (!team) return [];
    const id = `${row.id}/${c.id}`;
    return [
      {
        id,
        assetId: row.id,
        version: row.version,
        kind: 'scene' as const,
        name: typeof c.name === 'string' ? c.name : '',
        person: personOf(c.sheet, id),
        folderId: row.folderId,
        castId: c.id,
        sceneName: row.name,
        sheet: { team },
      },
    ];
  });
}

/**
 * The teams a character editor offers to join (the Team tab's "Join an existing team"): the same list as the Teams
 * view, never the open asset's own record (its team is edited in place there).
 */
export const joinChoices = (members: readonly TeamMember[], editingId: string | null): TeamChoice[] =>
  choicesWithLeaders(members.filter((m) => m.assetId !== editingId).map((m) => ({ name: m.name, team: m.sheet.team })));

/** The first member whose record leads (the Teams view keeps one Leader box per person). */
const leaderIn = (members: readonly TeamMember[]): TeamMember | null =>
  members.find((x) => x.sheet.team.leads === true) ?? null;

/** Every team the members wear, each tag once, sorted by name. */
export function libraryTeams(members: readonly TeamMember[]): LibraryTeam[] {
  const teams = teamsIn(members);
  const conflicts = new Map(tagConflicts(members).map((c) => [c.tag, c.groups]));
  const nameOf = new Map(teams.map((t) => [t.tag, t.label]));
  return teams
    .map((t) => {
      const characters = t.members.filter((m) => m.kind === 'character').length;
      // A plain team cannot disagree on a block; a kill-team plays the block the table would (`playedBlock`), and a
      // member missing it - a sheet joined before - never switches the team off.
      const groups = t.killTeam ? blockGroups(t) : [];
      const leader = leaderIn(t.members);
      const killTeam = t.killTeam ? playedBlock(t, leader) : undefined;
      return {
        ...t,
        killTeam,
        record: teamInfoOf({ ...t, killTeam }),
        livery: liveryFor(t, members),
        ally: t.side !== t.tag ? (nameOf.get(t.side) ?? null) : null,
        characters,
        inScenes: t.members.length - characters,
        conflict: conflicts.get(t.tag) ?? null,
        blockConflict: groups.length > 1 ? groups : null,
        leader: leader ? { person: leader.person, name: leader.name } : null,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}
